import React from 'react';
import { Bell, UserCircle, X, Lightbulb } from 'lucide-react';
import { Toggle } from '../ui/toggle';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Section, FormField } from './InspectionShared';

const TasksSection = ({
  formData, setFormData, toggleArray, handleSectionPhoto,
  taskCategories, availableTaskTypes, beekeepers,
  showSuggestTask, setShowSuggestTask, suggestTaskName, setSuggestTaskName,
  suggestTaskCategory, setSuggestTaskCategory, handleSuggestTask, submittingSuggestion,
}) => {
  return (
    <Section title="Assign Tasks" icon={<Bell className="h-6 w-6 text-purple-600" />} onPhoto={handleSectionPhoto}>
      <p className="text-sm text-stone-500 mb-3">Select actions that need to be done - these will be added to your Dashboard reminders.</p>

      {taskCategories.length > 0 && availableTaskTypes.length > 0 ? (
        <div className="space-y-4">
          {taskCategories.map(category => {
            const categoryTasks = availableTaskTypes.filter(t => t.category === category.id);
            if (categoryTasks.length === 0) return null;
            const categoryColors = {
              red: 'border-red-200 bg-red-50', blue: 'border-blue-200 bg-blue-50',
              amber: 'border-amber-200 bg-amber-50', purple: 'border-purple-200 bg-purple-50',
              gray: 'border-gray-200 bg-gray-50', green: 'border-green-200 bg-green-50',
              orange: 'border-orange-200 bg-orange-50', yellow: 'border-yellow-200 bg-yellow-50',
              pink: 'border-pink-200 bg-pink-50', stone: 'border-stone-200 bg-stone-50',
            };
            const gridCols = categoryTasks.length === 1 ? 'grid-cols-1' : 'grid-cols-2 md:grid-cols-3';
            return (
              <div key={category.id} className={`p-3 rounded-xl border ${categoryColors[category.color] || categoryColors.stone}`}>
                <div className="text-xs font-semibold text-stone-600 mb-2">{category.name}</div>
                <div className={`grid ${gridCols} gap-2`}>
                  {categoryTasks.map((task) => (
                    <Toggle
                      key={task.id}
                      pressed={formData.actions_to_take.includes(task.name)}
                      onPressedChange={() => toggleArray('actions_to_take', task.name)}
                      data-testid={`action-todo-${task.name.toLowerCase().replace(/\s+/g, '-')}`}
                      className="h-auto min-h-[48px] py-2 px-3 text-sm data-[state=on]:bg-purple-600 data-[state=on]:text-white flex items-center justify-center text-center whitespace-nowrap"
                    >
                      {task.name}
                    </Toggle>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {['Mite Wash', 'Split Hive', 'Requeen', 'Add Feeders', 'Remove Feeders',
           'Add Pollen Subs', 'Add Nutrition', 'Add Beetle Trap', 'Add Entrance Reducer',
           'Add Excluder', 'Remove Excluder', 'Add Super', 'Remove Super', 'Pack Down for Winter',
           'Apiary Maintenance Required'].map((action) => (
            <Toggle
              key={action}
              pressed={formData.actions_to_take.includes(action)}
              onPressedChange={() => toggleArray('actions_to_take', action)}
              data-testid={`action-todo-${action.toLowerCase().replace(/\s+/g, '-')}`}
              className="h-14 text-sm data-[state=on]:bg-purple-600 data-[state=on]:text-white"
            >
              {action}
            </Toggle>
          ))}
        </div>
      )}

      {formData.actions_to_take.length > 0 && (
        <div className="mt-4 p-4 bg-purple-50 rounded-xl border border-purple-200">
          <FormField label="Assign Beekeeper to Tasks (Optional)">
            <div className="flex items-center gap-3">
              <UserCircle className="h-5 w-5 text-purple-600 flex-shrink-0" />
              <Select
                value={formData.actions_assigned_beekeeper || "unassigned"}
                onValueChange={(value) => setFormData({ ...formData, actions_assigned_beekeeper: value === "unassigned" ? "" : value })}
              >
                <SelectTrigger className="h-12 flex-1" data-testid="assign-beekeeper-select">
                  <SelectValue placeholder="Select beekeeper to assign these tasks" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned"><span className="text-stone-400">No assignment (general reminder)</span></SelectItem>
                  {beekeepers.map((beekeeper) => (
                    <SelectItem key={beekeeper.id} value={beekeeper.name}>{beekeeper.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.actions_assigned_beekeeper && (
                <Button type="button" variant="ghost" size="icon" onClick={() => setFormData({ ...formData, actions_assigned_beekeeper: '' })} className="text-stone-400 hover:text-stone-600">
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            <p className="text-xs text-purple-600 mt-2">
              {formData.actions_assigned_beekeeper
                ? `${formData.actions_to_take.length} task(s) will be assigned to ${formData.actions_assigned_beekeeper}`
                : `${formData.actions_to_take.length} task(s) selected - assign to a specific beekeeper or leave as general reminder`
              }
            </p>
          </FormField>
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-stone-200">
        {!showSuggestTask ? (
          <Button type="button" variant="outline" size="sm" onClick={() => setShowSuggestTask(true)} className="text-purple-600 border-purple-300 hover:bg-purple-50">
            <Lightbulb className="w-4 h-4 mr-2" /> Suggest New Task
          </Button>
        ) : (
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2 text-purple-800 font-medium"><Lightbulb className="w-4 h-4" /> Suggest a New Task</div>
            <p className="text-xs text-purple-700">Your suggestion will be sent to the admin for approval.</p>
            <div className="flex gap-2">
              <Input value={suggestTaskName} onChange={(e) => setSuggestTaskName(e.target.value)} placeholder="Task name (e.g., 'Check Ventilation')" className="flex-1" />
              <Select value={suggestTaskCategory} onValueChange={setSuggestTaskCategory}>
                <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {taskCategories.map(cat => (<SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button type="button" size="sm" onClick={handleSuggestTask} disabled={submittingSuggestion} className="bg-purple-600 hover:bg-purple-700">
                {submittingSuggestion ? 'Submitting...' : 'Submit Suggestion'}
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => { setShowSuggestTask(false); setSuggestTaskName(''); }}>Cancel</Button>
            </div>
          </div>
        )}
      </div>
    </Section>
  );
};

export default TasksSection;
